'use client'

import { useEffect, useState, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { SocketContext } from '@/lib/providers/socket-provider'
import {
    FiscalNote,
    getNote,
    cancelNote,
    syncNote,
    FISCAL_NOTE_TYPE_LABELS,
    FISCAL_NOTE_STATUS_LABELS,
    FISCAL_NOTE_STATUS_COLORS,
} from '@/lib/api/fiscal/fiscal-api'
import ConfirmModal from '@/components/confirm-modal'
import { MdArrowBack, MdSync, MdCancel, MdDownload, MdAttachMoney } from 'react-icons/md'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

const FiscalNoteDetailPage = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { canUpdate } = useResourcePermissions('fiscal', 'invoices')
    const { socket } = useContext(SocketContext)

    const [note, setNote] = useState<FiscalNote | null>(null)
    const [loading, setLoading] = useState(true)
    const [syncing, setSyncing] = useState(false)
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelLoading, setCancelLoading] = useState(false)
    const [justificativa, setJustificativa] = useState('')

    useEffect(() => {
        if (id) loadNote()
    }, [id])

    useEffect(() => {
        if (socket && id) {
            const event = `fiscal-note-updated-${id}`
            socket.on(event, (data: { note: FiscalNote }) => {
                setNote(data.note)
            })
            return () => { socket.off(event) }
        }
    }, [socket, id])

    const loadNote = async () => {
        setLoading(true)
        const response = await getNote(parseInt(id!))
        if (response.success) {
            setNote(response.result)
        } else {
            toast.error(response.error)
        }
        setLoading(false)
    }

    const handleSync = async () => {
        setSyncing(true)
        const response = await syncNote(parseInt(id!))
        if (response.success) {
            setNote(response.result)
            toast.success('Status atualizado')
        } else {
            toast.error(response.error)
        }
        setSyncing(false)
    }

    const handleCancel = async () => {
        if (!justificativa || justificativa.length < 15) {
            toast.error('A justificativa deve ter no mínimo 15 caracteres')
            return
        }
        setCancelLoading(true)
        const response = await cancelNote(parseInt(id!), justificativa)
        if (response.success) {
            setNote(response.result)
            toast.success('Nota cancelada com sucesso')
            setShowCancelModal(false)
            setJustificativa('')
        } else {
            toast.error(response.error)
        }
        setCancelLoading(false)
    }

    const formatCurrency = (cents: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(cents / 100)
    }

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        )
    }

    if (!note) {
        return (
            <div className="p-6">
                <p className="text-center text-base-content/60">Nota fiscal não encontrada</p>
            </div>
        )
    }

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate('/fiscal/invoices')}>
                        <MdArrowBack size={20} />
                    </button>
                    <h1 className="text-2xl font-bold">
                        {FISCAL_NOTE_TYPE_LABELS[note.type]} {note.numero ? `#${note.numero}` : ''}
                    </h1>
                    <span className={`badge ${FISCAL_NOTE_STATUS_COLORS[note.status]}`}>
                        {FISCAL_NOTE_STATUS_LABELS[note.status]}
                    </span>
                </div>
                <div className="flex gap-2">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={handleSync}
                        disabled={syncing}
                    >
                        {syncing ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSync size={18} />
                        )}
                        Sincronizar
                    </button>
                    {canUpdate && note.status === 'AUTHORIZED' && (
                        <button
                            className="btn btn-error btn-sm"
                            onClick={() => setShowCancelModal(true)}
                        >
                            <MdCancel size={18} />
                            Cancelar Nota
                        </button>
                    )}
                </div>
            </div>

            {/* Informações Gerais */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">Informações Gerais</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                        <div>
                            <span className="text-sm text-base-content/60">Tipo</span>
                            <p className="font-medium">{FISCAL_NOTE_TYPE_LABELS[note.type]}</p>
                        </div>
                        <div>
                            <span className="text-sm text-base-content/60">Referência</span>
                            <p className="font-medium font-mono text-sm">{note.ref}</p>
                        </div>
                        <div>
                            <span className="text-sm text-base-content/60">Valor Total</span>
                            <p className="font-bold text-secondary text-lg">{formatCurrency(note.valorTotal)}</p>
                        </div>
                        <div>
                            <span className="text-sm text-base-content/60">Data de Criação</span>
                            <p className="font-medium">
                                {format(new Date(note.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                            </p>
                        </div>
                        {note.dataEmissao && (
                            <div>
                                <span className="text-sm text-base-content/60">Data de Emissão</span>
                                <p className="font-medium">
                                    {format(new Date(note.dataEmissao), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                </p>
                            </div>
                        )}
                        {note.createdBy && (
                            <div>
                                <span className="text-sm text-base-content/60">Criado por</span>
                                <p className="font-medium">{note.createdBy.name}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Cobrança Vinculada */}
            {note.billing && (
                <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">
                            <MdAttachMoney className="text-xl" />
                            Cobrança Vinculada
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                            <div>
                                <span className="text-sm text-base-content/60">Cobrança</span>
                                <p className="font-medium">
                                    <button
                                        className="link link-primary"
                                        onClick={() => navigate(`/financial/billings/${note.billing!.id}`)}
                                    >
                                        #{note.billing.id}
                                    </button>
                                </p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Status</span>
                                <p>
                                    <span className={`badge badge-sm ${
                                        note.billing.status === 'PAID' ? 'badge-success' :
                                        note.billing.status === 'PENDING' ? 'badge-warning' :
                                        note.billing.status === 'OVERDUE' ? 'badge-error' :
                                        note.billing.status === 'CANCELLED' ? 'badge-ghost' :
                                        'badge-ghost'
                                    }`}>
                                        {note.billing.status === 'PAID' ? 'Pago' :
                                         note.billing.status === 'PENDING' ? 'Pendente' :
                                         note.billing.status === 'OVERDUE' ? 'Vencida' :
                                         note.billing.status === 'CANCELLED' ? 'Cancelada' :
                                         note.billing.status}
                                    </span>
                                </p>
                            </div>
                            <div>
                                <span className="text-sm text-base-content/60">Valor da Cobrança</span>
                                <p className="font-medium">{formatCurrency(note.billing.amount)}</p>
                            </div>
                            {note.billing.paymentMethod && (
                                <div>
                                    <span className="text-sm text-base-content/60">Método de Pagamento</span>
                                    <p className="font-medium">
                                        {note.billing.paymentMethod === 'PIX' ? 'PIX' :
                                         note.billing.paymentMethod === 'BOLETO' ? 'Boleto' :
                                         note.billing.paymentMethod === 'CREDIT_CARD' ? 'Cartão de Crédito' :
                                         note.billing.paymentMethod === 'DEBIT_CARD' ? 'Cartão de Débito' :
                                         note.billing.paymentMethod === 'CASH' ? 'Dinheiro' :
                                         note.billing.paymentMethod === 'BANK_TRANSFER' ? 'Transferência' :
                                         note.billing.paymentMethod}
                                    </p>
                                </div>
                            )}
                            <div>
                                <span className="text-sm text-base-content/60">Vencimento</span>
                                <p className="font-medium">
                                    {format(new Date(note.billing.dueDate), 'dd/MM/yyyy', { locale: ptBR })}
                                </p>
                            </div>
                            {note.billing.paidAt && (
                                <div>
                                    <span className="text-sm text-base-content/60">Data do Pagamento</span>
                                    <p className="font-medium">
                                        {format(new Date(note.billing.paidAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Destinatário/Tomador */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">
                        {note.type === 'NFSE' ? 'Tomador' : 'Destinatário'}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                        <div>
                            <span className="text-sm text-base-content/60">CNPJ/CPF</span>
                            <p className="font-medium">{note.destinatarioCnpjCpf || '-'}</p>
                        </div>
                        <div>
                            <span className="text-sm text-base-content/60">Nome</span>
                            <p className="font-medium">{note.destinatarioNome || '-'}</p>
                        </div>
                        {note.client && (
                            <div>
                                <span className="text-sm text-base-content/60">Cliente vinculado</span>
                                <p className="font-medium">{note.client.name}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Dados Fiscais */}
            <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                <div className="card-body">
                    <h2 className="card-title text-lg mb-4">Dados Fiscais</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-6">
                        {note.numero && (
                            <div>
                                <span className="text-sm text-base-content/60">Número</span>
                                <p className="font-medium">{note.numero}</p>
                            </div>
                        )}
                        {note.serie && (
                            <div>
                                <span className="text-sm text-base-content/60">Série</span>
                                <p className="font-medium">{note.serie}</p>
                            </div>
                        )}
                        {note.chaveNfe && (
                            <div className="md:col-span-2">
                                <span className="text-sm text-base-content/60">Chave NF-e</span>
                                <p className="font-medium font-mono text-xs break-all">{note.chaveNfe}</p>
                            </div>
                        )}
                        {note.protocolo && (
                            <div>
                                <span className="text-sm text-base-content/60">Protocolo</span>
                                <p className="font-medium">{note.protocolo}</p>
                            </div>
                        )}
                        {note.statusSefaz && (
                            <div>
                                <span className="text-sm text-base-content/60">Status SEFAZ</span>
                                <p className="font-medium">{note.statusSefaz}</p>
                            </div>
                        )}
                        {note.mensagemSefaz && (
                            <div className="md:col-span-2">
                                <span className="text-sm text-base-content/60">Mensagem SEFAZ</span>
                                <p className="font-medium">{note.mensagemSefaz}</p>
                            </div>
                        )}
                        {note.numeroRps && (
                            <div>
                                <span className="text-sm text-base-content/60">Número RPS</span>
                                <p className="font-medium">{note.numeroRps}</p>
                            </div>
                        )}
                        {note.codigoVerificacao && (
                            <div>
                                <span className="text-sm text-base-content/60">Código de Verificação</span>
                                <p className="font-medium">{note.codigoVerificacao}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Downloads */}
            {(note.pdfUrl || note.xmlUrl) && (
                <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Downloads</h2>
                        <div className="flex gap-3">
                            {note.pdfUrl && (
                                <a
                                    href={note.pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline btn-sm"
                                >
                                    <MdDownload size={18} />
                                    Baixar PDF
                                </a>
                            )}
                            {note.xmlUrl && (
                                <a
                                    href={note.xmlUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-outline btn-sm"
                                >
                                    <MdDownload size={18} />
                                    Baixar XML
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Descrição */}
            {note.descricao && (
                <div className="card bg-base-100 shadow-sm border border-base-200 mb-6">
                    <div className="card-body">
                        <h2 className="card-title text-lg mb-4">Descrição</h2>
                        <p className="whitespace-pre-wrap">{note.descricao}</p>
                    </div>
                </div>
            )}

            {/* Erro */}
            {note.errorMessage && (
                <div className="alert alert-error mb-6">
                    <div>
                        <h3 className="font-bold">Erro</h3>
                        <p>{note.errorMessage}</p>
                    </div>
                </div>
            )}

            {/* Cancel Modal */}
            {showCancelModal && (
                <div className="modal modal-open">
                    <div className="modal-box">
                        <h3 className="font-bold text-lg mb-4">Cancelar Nota Fiscal</h3>
                        <p className="text-sm text-base-content/70 mb-4">
                            Informe a justificativa para o cancelamento (mínimo 15 caracteres).
                        </p>
                        <div className="form-control">
                            <label className="label">
                                <span className="label-text">Justificativa *</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered h-24"
                                value={justificativa}
                                onChange={(e) => setJustificativa(e.target.value)}
                                placeholder="Informe o motivo do cancelamento..."
                            />
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost"
                                onClick={() => { setShowCancelModal(false); setJustificativa('') }}
                            >
                                Voltar
                            </button>
                            <button
                                className="btn btn-error"
                                onClick={handleCancel}
                                disabled={cancelLoading}
                            >
                                {cancelLoading && <span className="loading loading-spinner loading-sm"></span>}
                                Cancelar Nota
                            </button>
                        </div>
                    </div>
                    <div className="modal-backdrop" onClick={() => setShowCancelModal(false)}></div>
                </div>
            )}
        </div>
    )
}

export default FiscalNoteDetailPage
